import React, { useMemo, useState } from 'react';
import AddOutlined from '@mui/icons-material/AddOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '../../../../../components/ui';
import { useFeatureTheme } from '../../../../../theme/hooks/useFeatureTheme';
import { FORM_STUDIO_COPY } from '../../constants/formStudioConfig';
import { getSubstatusFromRow } from '../../utils/statusTriggerUtils';
import { hydrateTriggerForEdit } from '../../utils/triggerFormUtils';
import TriggerEditorDrawer from './TriggerEditorDrawer';
import TriggerSummaryCard from './TriggerSummaryCard';

export default function SubStatusTriggerBlock({
  statusRow,
  statusOptionName,
  subStatusField,
  subOptionName,
  workspaceId,
  disabled = false,
  isSaving = false,
  onSaveSubstatusTriggers,
}) {
  const { semantic, radiusPx, typographyStyles } = useFeatureTheme();
  const copy = FORM_STUDIO_COPY.automations.statusTriggers;
  const subFieldKey = subStatusField?.key_name || '';
  const subFieldLabel = subStatusField?.alias_name || subFieldKey || 'Sub-status';
  const subRow = getSubstatusFromRow(statusRow, subOptionName, subFieldKey);
  const triggers = Array.isArray(subRow?.triggers) ? subRow.triggers : [];

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const contextLabel = `${statusOptionName} · ${subOptionName} · ${subFieldLabel}`;

  const drawerTrigger = useMemo(() => {
    if (!drawerOpen) return null;
    if (editingIndex == null) return null;
    if (editingIndex === 'create') return null;
    return hydrateTriggerForEdit(triggers[editingIndex]);
  }, [drawerOpen, editingIndex, triggers]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingIndex(null);
  };

  const openCreate = () => {
    setEditingIndex('create');
    setDrawerOpen(true);
  };

  const openEdit = (index) => {
    setEditingIndex(index);
    setDrawerOpen(true);
  };

  const handleSave = async (draft, index) => {
    const next = [...triggers];
    if (index == null || index === 'create') {
      next.push(draft);
    } else {
      next[index] = draft;
    }
    const result = await onSaveSubstatusTriggers?.(subOptionName, subFieldKey, next);
    if (result?.success !== false) {
      closeDrawer();
    }
  };

  const handleDelete = async (index) => {
    const next = triggers.filter((_, itemIndex) => itemIndex !== index);
    await onSaveSubstatusTriggers?.(subOptionName, subFieldKey, next);
  };

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: radiusPx.sm,
        border: `1px solid ${semantic.borders.border}`,
        bgcolor: semantic.surfaces.surfaceSubtle,
      }}
    >
      <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600, fontSize: 13, mb: 0.5 }}>
        {subOptionName}
      </Typography>
      <Typography sx={{ ...typographyStyles.caption, color: semantic.text.secondary, mb: 1 }}>
        {subFieldLabel}
      </Typography>
      <Stack spacing={1}>
        {triggers.map((trigger, index) => (
          <TriggerSummaryCard
            key={`sub-trigger-${subOptionName}-${index}`}
            trigger={trigger}
            disabled={disabled || isSaving}
            onEdit={() => openEdit(index)}
            onDelete={() => handleDelete(index)}
          />
        ))}
        {triggers.length === 0 ? (
          <Typography sx={{ ...typographyStyles.caption, color: semantic.text.secondary }}>
            {copy.noTriggers}
          </Typography>
        ) : null}
        <Button
          variant="outline"
          icon={<AddOutlined />}
          disabled={disabled || isSaving}
          onClick={openCreate}
        >
          {copy.addTrigger}
        </Button>
      </Stack>

      <TriggerEditorDrawer
        open={drawerOpen}
        contextLabel={contextLabel}
        trigger={drawerTrigger}
        triggerIndex={editingIndex === 'create' ? null : editingIndex}
        workspaceId={workspaceId}
        isSaving={isSaving}
        onClose={closeDrawer}
        onSave={handleSave}
      />
    </Box>
  );
}
